import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect } from 'react';
import { View, ActivityIndicator, TouchableOpacity, TextInput, ScrollView } from 'react-native';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { apiRequest } from '../../services/apiClient';
import { brand } from '../../design-system/theme/tokens';
import { useTranslation } from '../../hooks/useTranslation';

interface SchoolAIPolicyEditorProps {
  schoolId: number; // caller passes the authenticated user's school_id
}

export default function SchoolAIPolicyEditor({ schoolId }: SchoolAIPolicyEditorProps) {
  const { t } = useTranslation();
  // Behavior section
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [behaviorSaving, setBehaviorSaving] = useState(false);
  const [harshness, setHarshness] = useState('0.5');
  const [narrativeStyle, setNarrativeStyle] = useState('balanced');
  const [detailLevel, setDetailLevel] = useState('standard');
  const [behaviorSaved, setBehaviorSaved] = useState(false);

  // Knowledge section
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);
  const [knowledgePackIds, setKnowledgePackIds] = useState(''); // comma-separated string for editing
  const [frameworkSourceIds, setFrameworkSourceIds] = useState(''); // comma-separated string for editing
  const [knowledgeSaved, setKnowledgeSaved] = useState(false);

  // Load behavior on mount
  useEffect(() => {
    setBehaviorLoading(true);
    apiRequest<any>({ endpoint: `/schools/${schoolId}/ai-policy/behavior`, method: 'GET' })
      .then((data: any) => {
        setHarshness(String(data.grading_harshness ?? 0.5));
        setNarrativeStyle(data.narrative_style ?? 'balanced');
        setDetailLevel(data.feedback_detail_level ?? 'standard');
      })
      .catch((e: Error) => {
        console.error('Error loading school behavior policy:', e);
        showDialog(t('schoolAiPolicy.behaviorLoadError'), t('schoolAiPolicy.tryAgainLater'));
      })
      .finally(() => setBehaviorLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload per school, not per language (keeps unsaved edits)
  }, [schoolId]);

  // Load knowledge on mount
  useEffect(() => {
    setKnowledgeLoading(true);
    apiRequest<any>({ endpoint: `/schools/${schoolId}/ai-policy/knowledge`, method: 'GET' })
      .then((data: any) => {
        setKnowledgePackIds((data.knowledge_pack_ids ?? []).join(', '));
        setFrameworkSourceIds((data.framework_source_ids ?? []).join(', '));
      })
      .catch((e: Error) => {
        console.error('Error loading school knowledge policy:', e);
        showDialog(t('schoolAiPolicy.knowledgeLoadError'), t('schoolAiPolicy.tryAgainLater'));
      })
      .finally(() => setKnowledgeLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload per school, not per language (keeps unsaved edits)
  }, [schoolId]);

  const handleSaveBehavior = async () => {
    setBehaviorSaving(true);
    try {
      await apiRequest({
        endpoint: `/schools/${schoolId}/ai-policy/behavior`,
        method: 'PUT',
        data: {
          grading_harshness: parseFloat(harshness) || 0.5,
          narrative_style: narrativeStyle,
          feedback_detail_level: detailLevel,
        },
      });
      setBehaviorSaved(true);
    } catch (e: unknown) {
      console.error('Error saving school behavior policy:', e);
      showDialog(t('common.error'), t('schoolAiPolicy.behaviorSaveError'));
    } finally {
      setBehaviorSaving(false);
    }
  };

  const handleSaveKnowledge = async () => {
    setKnowledgeSaving(true);
    try {
      const packIds = knowledgePackIds.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
      const frameworkIds = frameworkSourceIds.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
      await apiRequest({
        endpoint: `/schools/${schoolId}/ai-policy/knowledge`,
        method: 'PUT',
        data: {
          knowledge_pack_ids: packIds,
          framework_source_ids: frameworkIds,
        },
      });
      setKnowledgeSaved(true);
    } catch (e: unknown) {
      console.error('Error saving school knowledge policy:', e);
      showDialog(t('common.error'), t('schoolAiPolicy.knowledgeSaveError'));
    } finally {
      setKnowledgeSaving(false);
    }
  };

  return (
    <ScrollView>
      {/* Section 1: AI Behavior Settings */}
      <Card className="mb-4">
        <CardHeader>
          <Text className="text-xl font-bold">{t('schoolAiPolicy.behaviorTitle')}</Text>
        </CardHeader>
        <CardContent>
          {behaviorLoading ? (
            <View className="items-center justify-center py-4">
              <ActivityIndicator size="small" color={brand.primary} />
            </View>
          ) : (
            <>
              {/* Grading Harshness */}
              <View className="mb-4">
                <Text className="text-sm font-medium text-gray-700 mb-1">{t('schoolAiPolicy.gradingHarshness')}</Text>
                <TextInput
                  value={harshness}
                  onChangeText={setHarshness}
                  placeholder={t('schoolAiPolicy.gradingHarshnessPlaceholder')}
                  placeholderTextColor="#9CA3AF"
                  keyboardType="decimal-pad"
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              {/* Narrative Style */}
              <View className="mb-4">
                <Text className="text-sm font-medium text-gray-700 mb-2">{t('schoolAiPolicy.narrativeStyle')}</Text>
                <View className="flex-row flex-wrap">
                  {(['balanced', 'encouraging', 'direct'] as const).map((opt) => (
                    <TouchableOpacity
                      key={opt}
                      onPress={() => setNarrativeStyle(opt)}
                      className={`px-3 py-1 rounded-full mr-2 mb-2 ${narrativeStyle === opt ? 'bg-blue-600' : 'bg-gray-100'}`}
                    >
                      <Text className={narrativeStyle === opt ? 'text-white text-sm' : 'text-gray-700 text-sm'}>
                        {t(`schoolAiPolicy.narrativeStyles.${opt}`)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Feedback Detail Level */}
              <View className="mb-4">
                <Text className="text-sm font-medium text-gray-700 mb-2">{t('schoolAiPolicy.feedbackDetailLevel')}</Text>
                <View className="flex-row flex-wrap">
                  {(['brief', 'standard', 'detailed'] as const).map((opt) => (
                    <TouchableOpacity
                      key={opt}
                      onPress={() => setDetailLevel(opt)}
                      className={`px-3 py-1 rounded-full mr-2 mb-2 ${detailLevel === opt ? 'bg-blue-600' : 'bg-gray-100'}`}
                    >
                      <Text className={detailLevel === opt ? 'text-white text-sm' : 'text-gray-700 text-sm'}>
                        {t(`schoolAiPolicy.detailLevels.${opt}`)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              <Button
                onPress={handleSaveBehavior}
                disabled={behaviorSaving}
                className={behaviorSaving ? 'bg-gray-400' : 'bg-blue-600'}
              >
                <Text className="text-white font-medium">
                  {behaviorSaving ? t('schoolAiPolicy.saving') : t('schoolAiPolicy.saveBehavior')}
                </Text>
              </Button>

              {behaviorSaved && (
                <Text className="text-green-600 text-sm mt-2">{t('schoolAiPolicy.behaviorSaved')}</Text>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Knowledge Bindings */}
      <Card className="mb-4">
        <CardHeader>
          <Text className="text-xl font-bold">{t('schoolAiPolicy.knowledgeTitle')}</Text>
        </CardHeader>
        <CardContent>
          {knowledgeLoading ? (
            <View className="items-center justify-center py-4">
              <ActivityIndicator size="small" color={brand.primary} />
            </View>
          ) : (
            <>
              {/* Knowledge Pack IDs */}
              <View className="mb-4">
                <Text className="text-sm font-medium text-gray-700 mb-1">{t('schoolAiPolicy.knowledgePackIds')}</Text>
                <TextInput
                  value={knowledgePackIds}
                  onChangeText={setKnowledgePackIds}
                  placeholder={t('schoolAiPolicy.knowledgePackIdsPlaceholder')}
                  placeholderTextColor="#9CA3AF"
                  multiline
                  numberOfLines={3}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              {/* Framework Source IDs */}
              <View className="mb-4">
                <Text className="text-sm font-medium text-gray-700 mb-1">{t('schoolAiPolicy.frameworkSourceIds')}</Text>
                <TextInput
                  value={frameworkSourceIds}
                  onChangeText={setFrameworkSourceIds}
                  placeholder={t('schoolAiPolicy.frameworkSourceIdsPlaceholder')}
                  placeholderTextColor="#9CA3AF"
                  multiline
                  numberOfLines={3}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <Button
                onPress={handleSaveKnowledge}
                disabled={knowledgeSaving}
                className={knowledgeSaving ? 'bg-gray-400' : 'bg-blue-600'}
              >
                <Text className="text-white font-medium">
                  {knowledgeSaving ? t('schoolAiPolicy.saving') : t('schoolAiPolicy.saveKnowledge')}
                </Text>
              </Button>

              {knowledgeSaved && (
                <Text className="text-green-600 text-sm mt-2">{t('schoolAiPolicy.knowledgeSaved')}</Text>
              )}

              <Text className="text-gray-500 text-xs mt-3">
                {t('schoolAiPolicy.knowledgeHint')}
              </Text>
            </>
          )}
        </CardContent>
      </Card>
    </ScrollView>
  );
}
